// Preview before confirm, shared by chat's cards and the Cockpit's Decision Queue (docs/cockpit/spec.md):
// anything irreversible or sent outside the business shows exactly what will happen first. An invoice
// shows as it will be issued and says it can't be edited once issued. A message is editable, offers
// Call instead, and once sent waits UNDO_SECONDS so it can be undone: nothing reaches the API until then.
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { MessagePreview, Preview } from "../api.js";
import { useWords } from "../i18n/words.js";
import { MessageHeader, PreviewView } from "./Preview.js";

/** How long a sent message can still be undone. */
export const UNDO_SECONDS = 10;

const telOf = (phone: string) => `tel:${phone.replace(/[^+\d]/g, "")}`;

function MessageAction({
  message,
  canSend,
  busy,
  onSend,
  children,
}: {
  message: MessagePreview;
  canSend: boolean;
  busy: boolean;
  onSend: (text: string) => void;
  children?: ReactNode;
}) {
  const w = useWords();
  const [text, setText] = useState(message.text);
  const [left, setLeft] = useState<number | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const field = useRef<HTMLTextAreaElement>(null);
  const undoButton = useRef<HTMLButtonElement>(null);
  const fieldId = useId();
  const words = text.trim();
  const sending = left !== undefined;

  // The countdown: at zero the message goes to the API. Leaving before then sends nothing.
  const send = useRef(() => onSend(words));
  send.current = () => onSend(words);
  useEffect(() => {
    if (left === undefined) return;
    if (left === 0) {
      setLeft(undefined);
      send.current();
      return;
    }
    const tick = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(tick);
  }, [left]);

  useEffect(() => {
    if (sending) undoButton.current?.focus();
  }, [sending]);

  const undo = () => {
    setLeft(undefined);
    setNotice(w.previewNotSent(message.to.name));
    field.current?.focus();
  };

  return (
    <>
      <div className="doc">
        <MessageHeader message={message} />
        {canSend ? (
          <label className="field" htmlFor={fieldId}>
            {w.previewMessageTo(message.to.name)}
            <textarea
              id={fieldId}
              ref={field}
              rows={4}
              maxLength={2000}
              value={text}
              readOnly={sending}
              onChange={(event) => setText(event.target.value)}
            />
          </label>
        ) : (
          <blockquote className="message">{message.text}</blockquote>
        )}
      </div>
      {sending ? (
        <div className="undo" role="status">
          <span>{w.previewSendingIn(message.to.name, left)}</span>
          <button ref={undoButton} type="button" className="btn" onClick={undo}>
            {w.previewUndo}
          </button>
        </div>
      ) : notice ? (
        <p role="status">{notice}</p>
      ) : null}
      <div className="acts">
        {canSend && !sending ? (
          <button
            type="button"
            className="btn btn-go"
            disabled={busy || words === ""}
            onClick={() => {
              setNotice(undefined);
              setLeft(UNDO_SECONDS);
            }}
          >
            {w.previewSendTo(message.to.name)}
          </button>
        ) : null}
        {message.to.phone ? (
          <a className="btn" href={telOf(message.to.phone)}>
            {w.previewCallInstead(message.to.name)}
          </a>
        ) : null}
        {sending ? null : children}
      </div>
    </>
  );
}

export interface ActionPreviewProps {
  readonly preview: Preview;
  /** The person's Roles let them confirm it now. */
  readonly canConfirm: boolean;
  readonly busy: boolean;
  /** Confirms it; for a message, with its words as the person left them, once the undo window has passed. */
  readonly onConfirm: (text?: string) => void;
  /** The other actions offered beside Confirm. */
  readonly children?: ReactNode;
}

export function ActionPreview({ preview, canConfirm, busy, onConfirm, children }: ActionPreviewProps) {
  const w = useWords();
  if (preview.kind === "message") {
    return (
      <MessageAction message={preview} canSend={canConfirm} busy={busy} onSend={onConfirm}>
        {children}
      </MessageAction>
    );
  }
  const invoice = preview.kind === "invoice" ? preview : undefined;
  const problems = invoice?.problems ?? [];
  return (
    <>
      <PreviewView preview={preview} />
      {problems.length > 0 ? (
        <p className="warn" role="note">
          {w.previewCantIssue(problems.join("; "))}
        </p>
      ) : null}
      {invoice && canConfirm ? <p className="warn">{w.previewCantEdit}</p> : null}
      <div className="acts">
        {canConfirm ? (
          <button
            type="button"
            className="btn btn-go"
            disabled={busy || problems.length > 0}
            onClick={() => onConfirm()}
          >
            {invoice ? w.previewConfirmIssue : w.previewConfirm}
          </button>
        ) : null}
        {children}
      </div>
    </>
  );
}
